import React from 'react';

const ConfigForm = ({
                        activeTab,
                        isLoadingConfig,
                        configData,
                        onConfigChange,
                        onSaveConfig,
                        isSavingConfig,
                        onTestNiraIntegration,
                        isTestingNiraIntegration,
                        onResetNiraPassword,
                        isResettingNiraPassword,
                        onGenerateSSHKey,
                        isGeneratingStanbicSSHKey,
                        onGeneratePGPKey,
                        isGeneratingStanbicPGPKey,
                        onTestConnection,
                        isTestingConnection,
                        onReloadConfig,
                        renderYesNoDropdown,
                        // StanChart-specific props
                        onTestStanchartConnection,
                        isTestingStanchartConnection,
                        onActivateStanchartService,
                        isActivatingStanchartService,
                    }) => {

    const renderNiraConfig = () => (
        <div className="config-view">

            {/* ============================================================
                OAuth 2.0 Configuration (used to obtain Bearer token for NIRA API)
               ============================================================ */}
            <div className="form-section">
                <h3><i className="fas fa-shield-alt"></i> NIRA API OAuth 2.0 Configuration</h3>
                <p className="form-hint">
                    Consumer Key + Secret Key are Base64-encoded as
                    <code> consumerKey:secretKey </code>
                    and sent to the token endpoint using the
                    <code> client_credentials </code> grant to obtain a Bearer token for the main NIRA API.
                </p>

                <div className="two-column-grid">
                    <div className="form-group">
                        <label>Token Base URL *</label>
                        <input
                            type="text"
                            value={configData.nira.tokenBaseUrl || ''}
                            onChange={(e) => onConfigChange('nira', 'tokenBaseUrl', e.target.value)}
                            placeholder="https://api-uat.integration.go.ug"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Consumer Key *</label>
                        <input
                            type="password"
                            value={configData.nira.consumerKey || ''}
                            onChange={(e) => onConfigChange('nira', 'consumerKey', e.target.value)}
                            autoComplete="new-password"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Secret Key *</label>
                        <input
                            type="password"
                            value={configData.nira.secretKey || ''}
                            onChange={(e) => onConfigChange('nira', 'secretKey', e.target.value)}
                            autoComplete="new-password"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Access Token Expiry</label>
                        <input
                            value={configData.nira.tokenExpiry || 'Not yet obtained'}
                            disabled
                        />
                    </div>
                </div>
            </div>

            {/* ============================================================
                API Configuration (REST base URL)
               ============================================================ */}
            <div className="form-section">
                <h3><i className="fas fa-cog"></i> NIRA API Configuration</h3>
                <div className="two-column-grid">
                    <div className="form-group full-width">
                        <label>API Base URL *</label>
                        <input
                            type="text"
                            value={configData.nira.url || ''}
                            onChange={(e) => onConfigChange('nira', 'url', e.target.value)}
                            placeholder="https://api-uat.integration.go.ug"
                            required
                        />
                        <small className="form-hint">
                            All resource calls go to
                            <code> {'{url}'}/t/nira.go.ug/nira-api/1.0.0/{'{resource}'} </code>
                        </small>
                    </div>
                </div>
            </div>

            {/* ============================================================
                NITA-AUTH API Configuration (Header Generator)
               ============================================================ */}
            <div className="form-section">
                <h3><i className="fas fa-key"></i> NITA-AUTH Header Generator</h3>
                <p className="form-hint">
                    These credentials are used to call the <code>/api/v1/access</code> endpoint
                    to generate the <code>nira-auth-forward</code>, <code>nira-nonce</code>, and <code>nira-created</code> headers.
                </p>

                <div className="two-column-grid">
                    <div className="form-group">
                        <label>NITA-AUTH Consumer Key *</label>
                        <input
                            type="password"
                            value={configData.nira.authConsumerKey || ''}
                            onChange={(e) => onConfigChange('nira', 'authConsumerKey', e.target.value)}
                            autoComplete="new-password"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>NITA-AUTH Secret Key *</label>
                        <input
                            type="password"
                            value={configData.nira.authSecretKey || ''}
                            onChange={(e) => onConfigChange('nira', 'authSecretKey', e.target.value)}
                            autoComplete="new-password"
                            required
                        />
                    </div>
                </div>
            </div>

            {/* ============================================================
                WSSE Backend Authentication (NIRA Credentials)
               ============================================================ */}
            <div className="form-section">
                <h3><i className="fas fa-lock"></i> NIRA Backend Credentials</h3>
                <p className="form-hint">
                    Username + Password are passed to the NITA-AUTH endpoint to securely generate the WSSE headers.
                </p>

                <div className="two-column-grid">
                    <div className="form-group">
                        <label>Username *</label>
                        <input
                            type="text"
                            value={configData.nira.username || ''}
                            onChange={(e) => onConfigChange('nira', 'username', e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Password *</label>
                        <input
                            type="password"
                            value={configData.nira.password || ''}
                            onChange={(e) => onConfigChange('nira', 'password', e.target.value)}
                            autoComplete="new-password"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Password Days Left</label>
                        <input
                            value={configData.nira.passwordDaysLeft || ''}
                            disabled
                        />
                        <small className="form-hint">
                            Updated automatically after each NIRA response.
                        </small>
                    </div>
                </div>
            </div>

            {/* ... rest of the NIRA config form (Sync Settings, Audit, Actions) stays the same ... */}

            <div className="form-actions">
                <button
                    onClick={() => onSaveConfig('nira')}
                    disabled={isSavingConfig}
                    className="btn btn-primary"
                >
                    {isSavingConfig ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Saving...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-save"></i> Save Configuration
                        </>
                    )}
                </button>

                <button
                    onClick={onTestNiraIntegration}
                    disabled={isTestingNiraIntegration}
                    className="btn btn-secondary"
                >
                    {isTestingNiraIntegration ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Verifying Identity...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-key"></i> Test NIRA Integration
                        </>
                    )}
                </button>

                <button
                    onClick={onResetNiraPassword}
                    disabled={isResettingNiraPassword}
                    className="btn btn-secondary"
                >
                    {isResettingNiraPassword ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Resetting Password...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-unlock-alt"></i> Reset NIRA Password
                        </>
                    )}
                </button>

                <button
                    onClick={() => onReloadConfig('nira')}
                    className="btn btn-tertiary"
                >
                    <i className="fas fa-sync"></i> Reload Configuration
                </button>
            </div>
        </div>
    );
    

    const renderStanbicConfig = () => (
        <div className="config-view">
            <div className="form-section">
                <h3><i className="fas fa-cog"></i> Basic Configuration</h3>
                <div className="two-column-grid">
                    <div className="form-group">
                        <label>Scheme Code</label>
                        <input
                            type="text"
                            value={configData.stanbic.schemeCode}
                            onChange={(e) => onConfigChange('stanbic', 'schemeCode', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>User ID</label>
                        <input
                            type="text"
                            value={configData.stanbic.userId}
                            onChange={(e) => onConfigChange('stanbic', 'userId', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Customer ID</label>
                        <input
                            type="text"
                            value={configData.stanbic.customerId}
                            onChange={(e) => onConfigChange('stanbic', 'customerId', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Instruction Type</label>
                        <select
                            value={configData.stanbic.instructionType}
                            onChange={(e) => onConfigChange('stanbic', 'instructionType', e.target.value)}
                        >
                            <option value="CREDIT_TRANSFER">Credit Transfer Instruction (Pain 001)</option>
                            <option value="DIRECT_DEBIT">Direct Debit/Collection (Pain 008)</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="form-section">
                <h3><i className="fas fa-folder"></i> File System Configuration</h3>
                <div className="two-column-grid">
                    <div className="form-group">
                        <label>Local SSH Server IP Address</label>
                        <input
                            type="text"
                            value={configData.stanbic.sshServerAddress}
                            onChange={(e) => onConfigChange('stanbic', 'sshServerAddress', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Local SSH Server Username</label>
                        <input
                            type="text"
                            value={configData.stanbic.sshServerUsername}
                            onChange={(e) => onConfigChange('stanbic', 'sshServerUsername', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Local SSH Server Password</label>
                        <input
                            type="password"
                            value={configData.stanbic.sshServerPassword}
                            onChange={(e) => onConfigChange('stanbic', 'sshServerPassword', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Local SSH Integration Folder</label>
                        <input
                            type="text"
                            value={configData.stanbic.integrationFolder}
                            onChange={(e) => onConfigChange('stanbic', 'integrationFolder', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Local SSH Key Generation Password</label>
                        <input
                            type="password"
                            value={configData.stanbic.generateSshKeyPassword}
                            onChange={(e) => onConfigChange('stanbic', 'generateSshKeyPassword', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Local Known Hosts Path</label>
                        <input
                            type="text"
                            readOnly
                            value={configData.stanbic.sftpKnownHostsPath}
                            onChange={(e) => onConfigChange('stanbic', 'sftpKnownHostsPath', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Local Private Key Path</label>
                        <input
                            type="text"
                            readOnly
                            value={configData.stanbic.sftpPrivateKeyPath}
                            onChange={(e) => onConfigChange('stanbic', 'sftpPrivateKeyPath', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>File Name Prefix</label>
                        <input
                            type="text"
                            readOnly
                            value={configData.stanbic.fileNamePrefix}
                            onChange={(e) => onConfigChange('stanbic', 'fileNamePrefix', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="form-section">
                <h3><i className="fas fa-lock"></i> Encryption Configuration</h3>
                <div className="two-column-grid">
                    {renderYesNoDropdown('stanbic', 'enableEncryption', 'Enable Encryption')}
                    <div className="form-group">
                        <label>Local PGP Key Company Name *</label>
                        <input
                            type="text"
                            value={configData.stanbic.localPgpKeyCompanyName}
                            onChange={(e) => onConfigChange('stanbic', 'localPgpKeyCompanyName', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Local PGP Key Email *</label>
                        <input
                            type="email"
                            value={configData.stanbic.localPgpKeyEmail}
                            onChange={(e) => onConfigChange('stanbic', 'localPgpKeyEmail', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Local PGP Key Password *</label>
                        <input
                            type="password"
                            value={configData.stanbic.localPgpKeyPassword}
                            onChange={(e) => onConfigChange('stanbic', 'localPgpKeyPassword', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Bank PGP Key Path</label>
                        <input
                            type="text"
                            value={configData.stanbic.bankPgpKeyPath}
                            onChange={(e) => onConfigChange('stanbic', 'bankPgpKeyPath', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Bank PGP Key Email</label>
                        <input
                            type="text"
                            value={configData.stanbic.bankPgpKeyEmail}
                            onChange={(e) => onConfigChange('stanbic', 'bankPgpKeyEmail', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="form-section">
                <h3><i className="fas fa-network-wired"></i> SFTP Configuration</h3>
                <div className="two-column-grid">
                    <div className="form-group">
                        <label>SFTP Username *</label>
                        <input
                            type="text"
                            value={configData.stanbic.sftpUsername}
                            onChange={(e) => onConfigChange('stanbic', 'sftpUsername', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>SFTP Remote Host *</label>
                        <input
                            type="text"
                            value={configData.stanbic.sftpRemoteHost}
                            onChange={(e) => onConfigChange('stanbic', 'sftpRemoteHost', e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>SFTP Remote Port</label>
                        <input
                            type="number"
                            value={configData.stanbic.sftpRemotePort}
                            onChange={(e) => onConfigChange('stanbic', 'sftpRemotePort', parseInt(e.target.value))}
                        />
                    </div>
                    <div className="form-group">
                        <label>Remote Outbox Path</label>
                        <input
                            type="text"
                            value={configData.stanbic.sftpRemoteOutboxPath}
                            onChange={(e) => onConfigChange('stanbic', 'sftpRemoteOutboxPath', e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Remote Inbox Path</label>
                        <input
                            type="text"
                            value={configData.stanbic.sftpRemoteInboxPath}
                            onChange={(e) => onConfigChange('stanbic', 'sftpRemoteInboxPath', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="form-section">
                <h3><i className="fas fa-sliders-h"></i> Advanced Settings</h3>
                <div className="two-column-grid">
                    {renderYesNoDropdown('stanbic', 'activateSftp', 'Activate SFTP')}
                    {renderYesNoDropdown('stanbic', 'activateIntegration', 'Activate Integration')}
                    {renderYesNoDropdown('stanbic', 'combineUserAndCustomerOnFileName', 'Combine User/Customer in Filename')}
                    {renderYesNoDropdown('stanbic', 'promoteToProduction', 'Promote to Production')}
                    {renderYesNoDropdown('stanbic', 'replaceSpecialChars', 'Replace Special Characters')}
                    {renderYesNoDropdown('stanbic', 'truncateTrailingDecimals', 'Truncate Trailing Decimals')}
                    <div className="form-group">
                        <label>Rounding Type</label>
                        <select
                            value={configData.stanbic.roundPaymentsTo}
                            onChange={(e) => onConfigChange('stanbic', 'roundPaymentsTo', e.target.value)}
                        >
                            <option value="NONE">No rounding</option>
                            <option value="NEAREST_SHILLING">Nearest Shilling</option>
                            <option value="NEAREST_TEN_CENTS">Nearest ten cents</option>
                            <option value="NEAREST_FIVE_CENTS">Nearest five cents</option>
                            <option value="ROUND_DOWN">Round down</option>
                            <option value="ROUND_UP">Round up</option>
                            <option value="NEAREST_FIFTY_CENTS">Nearest Fifty Cents</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="form-actions">
                <button
                    onClick={() => onSaveConfig('stanbic')}
                    disabled={isSavingConfig}
                    className="btn btn-primary"
                >
                    {isSavingConfig ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Saving...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-save"></i> Save Configuration
                        </>
                    )}
                </button>

                <button
                    onClick={onGenerateSSHKey}
                    disabled={isGeneratingStanbicSSHKey}
                    className="btn btn-secondary"
                >
                    {isGeneratingStanbicSSHKey ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Generating...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-key"></i> Generate New SSH Key
                        </>
                    )}
                </button>

                <button
                    onClick={onGeneratePGPKey}
                    disabled={isGeneratingStanbicPGPKey}
                    className="btn btn-secondary"
                >
                    {isGeneratingStanbicPGPKey ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Generating...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-key"></i> Generate New PGP Key
                        </>
                    )}
                </button>

                <button
                    onClick={onTestConnection}
                    disabled={isTestingConnection}
                    className="btn btn-secondary"
                >
                    {isTestingConnection ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Testing...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-plug"></i> Test Connection
                        </>
                    )}
                </button>

                <button
                    onClick={() => onReloadConfig('stanbic')}
                    className="btn btn-tertiary"
                >
                    <i className="fas fa-sync"></i> Reload Configuration
                </button>
            </div>
        </div>
    );

    const renderStanchartConfig = () => (
        <div className="config-view">
            {/* API Configuration */}
            <div className="form-section">
                <h3><i className="fas fa-code"></i> API Configuration</h3>
                <div className="two-column-grid">
                    <div className="form-group">
                        <label>API Base URL *</label>
                        <input
                            type="text"
                            value={configData.stanchart.apiUrl || ''}
                            onChange={(e) => onConfigChange('stanchart', 'apiUrl', e.target.value)}
                            placeholder="https://openbanking.sc.com"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Payment Initiation End Point *</label>
                        <input
                            type="text"
                            value={configData.stanchart.paymentInitiationEndPoint || ''}
                            onChange={(e) => onConfigChange('stanchart', 'paymentInitiationEndPoint', e.target.value)}
                            placeholder="/api/cui/l3-cms/uat2/openapi/payments/v2/initiate"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Check Payment Status End Point *</label>
                        <input
                            type="text"
                            value={configData.stanchart.paymentStatusEndPoint || ''}
                            onChange={(e) => onConfigChange('stanchart', 'paymentStatusEndPoint', e.target.value)}
                            placeholder="/api/cui/l3-cms/uat2/openapi/payments/v2/status"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>JWT Issuer</label>
                        <input
                            type="text"
                            value={configData.stanchart.jwtIssuer || ''}
                            onChange={(e) => onConfigChange('stanchart', 'jwtIssuer', e.target.value)}
                            placeholder="SCB"
                        />
                    </div>
                    <div className="form-group">
                        <label>JWT Audience</label>
                        <input
                            type="text"
                            value={configData.stanchart.jwtAudience || ''}
                            onChange={(e) => onConfigChange('stanchart', 'jwtAudience', e.target.value)}
                            placeholder="SCB-APIBanking"
                        />
                    </div>
                    <div className="form-group">
                        <label>JWT Expiry (Minutes)</label>
                        <input
                            type="number"
                            value={configData.stanchart.jwtExpiryMinutes || 5}
                            onChange={(e) => onConfigChange('stanchart', 'jwtExpiryMinutes', parseInt(e.target.value) || 5)}
                            min="1"
                            max="60"
                        />
                    </div>
                </div>
            </div>

            {/* Service Activation */}
            <div className="form-section">
                <h3><i className="fas fa-key"></i> Service Activation Setup</h3>
                <div className="form-grid">
                    <div className="form-group full-width">
                        <label>Activation URL</label>
                        <input
                            type="text"
                            value={configData.stanchart.activationUrl || ''}
                            onChange={(e) => onConfigChange('stanchart', 'activationUrl', e.target.value)}
                            placeholder="https://apitest.standardchartered.com/uat2/activate"
                        />
                        <small className="form-hint">
                            One-time activation endpoint. Must be completed within 30 days of receiving activation key.
                        </small>
                    </div>

                    <div className="form-group full-width">
                        <label>Activation Key (JSON Content)</label>
                        <textarea
                            value={configData.stanchart.activationKey || ''}
                            onChange={(e) => onConfigChange('stanchart', 'activationKey', e.target.value)}
                            placeholder='Paste the entire content from activationkey.json file'
                            rows="4"
                            className="activation-key-textarea"
                        />
                        <small className="form-hint">
                            Copy and paste the complete content from the activationKey.json file provided by Standard Chartered Bank
                        </small>
                    </div>
                </div>
            </div>

            {/* Webhook Configuration */}
            <div className="form-section">
                <h3><i className="fas fa-broadcast-tower"></i> Webhook Configuration</h3>
                <div className="form-grid">
                    <div className="form-group full-width">
                        <label>Webhook URL</label>
                        <input
                            type="url"
                            value={configData.stanchart.webhookUrl || ''}
                            onChange={(e) => onConfigChange('stanchart', 'webhookUrl', e.target.value)}
                            placeholder="https://your-domain.com/api/stanchart/webhook"
                        />
                    </div>
                    {renderYesNoDropdown('stanchart', 'enableWebhook', 'Enable Webhook Notifications')}
                </div>
            </div>

            {/* System Configuration */}
            <div className="form-section">
                <h3><i className="fas fa-sliders-h"></i> System Configuration</h3>
                <div className="two-column-grid">
                    {renderYesNoDropdown('stanchart', 'enableIntegration', 'Enable Integration')}
                    {renderYesNoDropdown('stanchart', 'promoteToProduction', 'Promote to Production')}
                    <div className="form-group">
                        <label>API Timeout (ms)</label>
                        <input
                            type="number"
                            value={configData.stanchart.jwtTimeoutSeconds || 30000}
                            onChange={(e) => onConfigChange('stanchart', 'jwtTimeoutSeconds', parseInt(e.target.value) || 30000)}
                            min="1000"
                            max="60000"
                        />
                    </div>
                </div>
            </div>

            <div className="form-actions">
                <button
                    onClick={() => onSaveConfig('stanchart')}
                    disabled={isSavingConfig}
                    className="btn btn-primary"
                >
                    {isSavingConfig ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Saving...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-save"></i> Save Configuration
                        </>
                    )}
                </button>

                <button
                    onClick={onTestStanchartConnection}
                    disabled={isTestingStanchartConnection}
                    className="btn btn-secondary"
                >
                    {isTestingStanchartConnection ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Testing...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-plug"></i> Test API Connection
                        </>
                    )}
                </button>

                <button
                    onClick={onActivateStanchartService}
                    disabled={isActivatingStanchartService || !configData.stanchart.activationKey}
                    className="btn btn-warning"
                >
                    {isActivatingStanchartService ? (
                        <>
                            <i className="fas fa-spinner fa-spin"></i> Activating...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-play-circle"></i> Activate Service
                        </>
                    )}
                </button>

                <button
                    onClick={() => onReloadConfig('stanchart')}
                    className="btn btn-tertiary"
                >
                    <i className="fas fa-sync"></i> Reload Configuration
                </button>
            </div>

            {!configData.stanchart.activationKey && (
                <div className="activation-warning">
                    <i className="fas fa-exclamation-triangle"></i>
                    <div>
                        <strong>Service Activation Required</strong>
                        <p>
                            You must provide the activation key received from Standard Chartered Bank
                            and activate the service before you can use the core APIs. This is a one-time
                            operation that must be completed within 30 days of receiving the activation key.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="config-section">
            {isLoadingConfig && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Loading configuration...</p>
                </div>
            )}

            {activeTab === 'nira' && renderNiraConfig()}
            {activeTab === 'stanbic' && renderStanbicConfig()}
            {activeTab === 'stanchart' && renderStanchartConfig()}
        </div>
    );
};

export default ConfigForm;